import { useEffect, useState } from "react";
import {
  createMidpointMediaCustomerTicket,
  listMidpointMediaCustomerTickets,
} from "../../api/partnerPortalApi";
import {
  badgeClass,
  formatStatusLabel,
} from "./midpointMediaCustomerPortalLabels";
import "../../styles/midpointMediaCustomerPortal.css";

const EMPTY_FORM = {
  subject: "",
  description: "",
  category: "infrastructure",
  priority: "medium",
};

export default function MidpointMediaCustomerTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadTickets = () =>
    listMidpointMediaCustomerTickets().then((body) => {
      setTickets(body.tickets || []);
      setMeta(body.meta || null);
    });

  useEffect(() => {
    let cancelled = false;
    loadTickets()
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(error.message || "Unable to load tickets.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await createMidpointMediaCustomerTicket(form);
      setForm(EMPTY_FORM);
      setSuccessMessage("Ticket submitted. Our team will follow up soon.");
      await loadTickets();
    } catch (error) {
      setErrorMessage(error.message || "Unable to create ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const categories = meta?.ticketCategories || [
    "infrastructure",
    "project",
    "access",
    "billing",
    "other",
  ];
  const priorities = meta?.ticketPriorities || ["low", "medium", "high", "urgent"];

  return (
    <div className="mm-customer-portal">
      <header className="mm-customer-portal__hero">
        <div>
          <p className="mm-customer-portal__eyebrow">Support</p>
          <h1>Tickets & issues</h1>
          <p className="mm-customer-portal__intro">
            Open a ticket for outages, access problems, project questions, or
            anything else Midpoint Media can help with.
          </p>
        </div>
      </header>

      <section className="mm-customer-panel">
        <h3>Report an issue</h3>
        <form className="mm-customer-form" onSubmit={onSubmit}>
          <label>
            Subject
            <input
              name="subject"
              value={form.subject}
              onChange={onChange}
              maxLength={180}
              required
              placeholder="Short summary of the issue"
            />
          </label>
          <div className="mm-customer-form__row">
            <label>
              Category
              <select name="category" value={form.category} onChange={onChange}>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {formatStatusLabel(category)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Priority
              <select name="priority" value={form.priority} onChange={onChange}>
                {priorities.map((priority) => (
                  <option key={priority} value={priority}>
                    {formatStatusLabel(priority)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Description
            <textarea
              name="description"
              value={form.description}
              onChange={onChange}
              maxLength={4000}
              required
              placeholder="What happened, where you saw it, and any impact on your team"
            />
          </label>
          {errorMessage ? <p className="mm-customer-error">{errorMessage}</p> : null}
          {successMessage ? <p className="mm-customer-muted">{successMessage}</p> : null}
          <div>
            <button
              type="submit"
              className="mm-customer-btn mm-customer-btn--primary"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit ticket"}
            </button>
          </div>
        </form>
      </section>

      <section className="mm-customer-panel">
        <h3>Your tickets</h3>
        {loading ? <p className="mm-customer-muted">Loading tickets…</p> : null}
        {!loading && tickets.length === 0 ? (
          <p className="mm-customer-empty">No tickets yet.</p>
        ) : null}
        {!loading && tickets.length > 0 ? (
          <div className="mm-customer-list">
            {tickets.map((ticket) => (
              <article key={ticket.id} className="mm-customer-row">
                <div className="mm-customer-row__top">
                  <strong>{ticket.subject}</strong>
                  <span className={badgeClass(ticket.status)}>
                    {formatStatusLabel(ticket.status)}
                  </span>
                </div>
                <p>
                  {ticket.id} · {formatStatusLabel(ticket.priority)} ·{" "}
                  {formatStatusLabel(ticket.category)}
                </p>
                {ticket.description ? <p>{ticket.description}</p> : null}
              </article>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}
